import React, { useState, useEffect } from 'react';
import { getLegacyApiUrl } from '../utils/apiUtils';
import StudentSidebar from '../components/StudentSidebar';

const StudentSettings = () => {
  const [studentInfo, setStudentInfo] = useState({
    full_name: '',
    email: '',
    phone: '',
    student_id: '',
    program: '',
    year_of_study: '',
    department: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchStudentInfo();
  }, []);

  const fetchStudentInfo = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Use the correct API URL
      const apiUrl = getLegacyApiUrl();
      
      const response = await fetch(`${apiUrl}/students/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Session expired. Please log in again.');
          return;
        }
        throw new Error('Failed to fetch profile information');
      }

      const data = await response.json();
      console.log('Profile data received:', data);
      
      setStudentInfo({
        full_name: data.student?.name || data.student?.full_name || '',
        email: data.student?.email || '',
        phone: data.student?.contact_no || data.student?.phone || '',
        student_id: data.student?.student_id || '',
        program: data.student?.degree_programme || data.student?.program || '',
        year_of_study: data.student?.year_of_study || '',
        department: data.student?.department || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStudentInfo(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      
      // Use the correct API URL
      const apiUrl = getLegacyApiUrl();
      
      // Only send phone number - ONLY allowed field
      const updateData = {
        phone: studentInfo.phone
      };
      
      console.log('Sending update data:', updateData);
      console.log('API URL:', `${apiUrl}/students/profile`);
      
      const response = await fetch(`${apiUrl}/students/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      const responseData = await response.json();
      console.log('Update response:', responseData);

      if (!response.ok) {
        if (response.status === 403) {
          setError(responseData.message || 'You can only update your phone number. Contact academic office for other changes.');
        } else if (response.status === 401) {
          setError('Session expired. Please log in again.');
        } else if (response.status === 400) {
          setError(responseData.message || 'Invalid phone number format.');
        } else {
          setError(responseData.message || 'Update failed');
        }
        return;
      }

      // Update local state with server response if successful
      if (responseData.success && responseData.student) {
        setStudentInfo(prev => ({
          ...prev,
          phone: responseData.student.phone || responseData.student.contact_no || ''
        }));
      }

      setSuccess('Phone number updated successfully! 🎉');
      setTimeout(() => setSuccess(''), 4000);
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.message.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Unable to save changes. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'S';
    return name.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F8FAFC' }}>
        <StudentSidebar />
        <div style={{ 
          marginLeft: 290, 
          flex: 1, 
          padding: '30px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            fontSize: 18,
            color: '#64748B',
            fontFamily: 'Outfit, sans-serif'
          }}>
            Loading settings...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F8FAFC' }}>
      <StudentSidebar />
      
      <div style={{ 
        marginLeft: 290, 
        flex: 1, 
        padding: '30px',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{
          marginBottom: 30,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{
              fontSize: 32,
              fontWeight: 700,
              color: '#1E293B',
              margin: 0,
              fontFamily: 'Outfit, sans-serif'
            }}>
              Settings
            </h1>
            <p style={{
              fontSize: 16,
              color: '#64748B',
              margin: '8px 0 8px 0',
              fontFamily: 'Outfit, sans-serif'
            }}>
              Manage your profile and account settings
            </p>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '12px 24px',
              backgroundColor: saving ? '#94A3B8' : '#F04915',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              fontFamily: 'Outfit, sans-serif',
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div style={{
            padding: 16,
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 8,
            color: '#DC2626',
            fontSize: 14,
            fontFamily: 'Outfit, sans-serif',
            marginBottom: 20
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: 16,
            backgroundColor: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: 8,
            color: '#166534',
            fontSize: 14,
            fontFamily: 'Outfit, sans-serif',
            marginBottom: 20
          }}>
            {success}
          </div>
        )}

        {/* Personal Information */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: 12,
          padding: 24,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          marginBottom: 20
        }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            color: '#1E293B',
            margin: '0 0 20px 0',
            fontFamily: 'Outfit, sans-serif'
          }}>
            Personal Information
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 20
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8,
                fontFamily: 'Outfit, sans-serif'
              }}>
                Full Name
              </label>
              <input
                type="text"
                name="full_name"
                value={studentInfo.full_name}
                readOnly
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #D1D5DB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'Outfit, sans-serif',
                  backgroundColor: '#F9FAFB',
                  color: '#6B7280',
                  cursor: 'not-allowed'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8,
                fontFamily: 'Outfit, sans-serif'
              }}>
                Email
              </label>
              <input
                type="email"
                name="email"
                value={studentInfo.email}
                readOnly
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #D1D5DB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'Outfit, sans-serif',
                  backgroundColor: '#F9FAFB',
                  color: '#6B7280',
                  cursor: 'not-allowed'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8,
                fontFamily: 'Outfit, sans-serif'
              }}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={studentInfo.phone}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #D1D5DB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'Outfit, sans-serif',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#F04915'}
                onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8,
                fontFamily: 'Outfit, sans-serif'
              }}>
                Student ID
              </label>
              <input
                type="text"
                name="student_id"
                value={studentInfo.student_id}
                readOnly
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #D1D5DB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'Outfit, sans-serif',
                  backgroundColor: '#F9FAFB',
                  color: '#6B7280',
                  cursor: 'not-allowed'
                }}
              />
            </div>
          </div>
        </div>

        {/* Academic Information */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: 12,
          padding: 24,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            color: '#1E293B',
            margin: '0 0 20px 0',
            fontFamily: 'Outfit, sans-serif'
          }}>
            Academic Information
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 20
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8,
                fontFamily: 'Outfit, sans-serif'
              }}>
                Program
              </label>
              <input
                type="text"
                name="program"
                value={studentInfo.program}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #D1D5DB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'Outfit, sans-serif',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#F04915'}
                onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8,
                fontFamily: 'Outfit, sans-serif'
              }}>
                Year of Study
              </label>
              <select
                name="year_of_study"
                value={studentInfo.year_of_study}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #D1D5DB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'Outfit, sans-serif',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: '#FFFFFF'
                }}
                onFocus={(e) => e.target.style.borderColor = '#F04915'}
                onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
              >
                <option value="">Select Year</option>
                <option value="1">1st Year</option>
                <option value="2">2nd Year</option>
                <option value="3">3rd Year</option>
                <option value="4">4th Year</option>
                <option value="5">5th Year</option>
                <option value="6">6th Year</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8,
                fontFamily: 'Outfit, sans-serif'
              }}>
                Department
              </label>
              <input
                type="text"
                name="department"
                value={studentInfo.department}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #D1D5DB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'Outfit, sans-serif',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#F04915'}
                onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
              />
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div style={{
          background: 'linear-gradient(135deg, #FF4E00 0%, #EC9F05 100%)',
          borderRadius: 12,
          padding: 24,
          marginTop: 20,
          color: 'white',
          textAlign: 'center'
        }}>
          <h4 style={{
            fontSize: 18,
            fontWeight: 600,
            margin: '0 0 12px 0',
            fontFamily: 'Outfit, sans-serif'
          }}>
            💡 Need Help?
          </h4>
          <p style={{
            fontSize: 14,
            margin: 0,
            fontFamily: 'Outfit, sans-serif',
            opacity: 0.9
          }}>
            If you need to update restricted fields like your name or email, please contact the academic office.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentSettings;
